import { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { useIntelligenceStatus } from '../hooks/useIntelligenceStatus';

const SiteContext = createContext({
  siteId: null,
  site: null,
  siteLoading: true,
  intelligence: { available: null, status: null, progress: null, loading: true },
});

export const useSite = () => useContext(SiteContext);

/**
 * Fetches site metadata and intelligence status.
 * Provides { siteId, site, siteLoading, intelligence } to the tree.
 * Placed inside the /:siteId layout route (AppShell).
 */
export const SiteProvider = ({ children }) => {
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(true);

  const siteReady = site?.status === 'ready';
  const intelligence = useIntelligenceStatus(siteReady ? siteId : null);

  useEffect(() => {
    if (!siteId) {
      setSite(null);
      setSiteLoading(false);
      return;
    }

    let cancelled = false;
    setSiteLoading(true);

    const fetchSite = async () => {
      try {
        const res = await apiRequest(`/api/website-rag/status/${siteId}`, { method: 'GET' });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setSite({
            websiteId: siteId,
            url: data.url,
            displayUrl: data.name || data.url,
            name: data.name,
            status: data.status,
            totalPages: data.progress?.pages_crawled || data.progress?.crawl?.pages_found || 0,
          });
        }
      } catch (err) {
        console.error('SiteProvider: failed to fetch site metadata', err);
        if (!cancelled) setSite(null);
      } finally {
        if (!cancelled) setSiteLoading(false);
      }
    };

    fetchSite();
    return () => { cancelled = true; };
  }, [siteId]);

  return (
    <SiteContext.Provider value={{ siteId, site, siteLoading, intelligence }}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
